import type { ComplianceSummary } from "../types";

/**
 * Reporting-window labels.
 *
 * The backend evaluates stats over a window that is normally the
 * calendar year to date but is floored at June 1, 2026 (see
 * WINDOW_FLOOR in fulfillment_dashboard/src/compliance.py). It ships
 * the window's first day as `summary.window_start`. These helpers turn
 * that into UI copy: "since Jun 1" while the floor is in effect, plain
 * "YTD" once the window is an ordinary calendar year again — so the
 * wording self-corrects in January 2027 with no frontend change.
 */

function parseIsoUtc(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

/**
 * First day of the reporting window. Falls back to the first heatmap
 * week for payloads cached before `window_start` shipped.
 */
export function windowStartDate(summary: ComplianceSummary): Date | null {
  return (
    parseIsoUtc(summary.window_start) ??
    parseIsoUtc(summary.heatmap?.weeks?.[0])
  );
}

/**
 * True when the window is an ordinary calendar YTD — it starts in the
 * first days of January (the first evaluated week can start as late as
 * Jan 7), or the payload carries no window info at all.
 */
export function isCalendarYtd(summary: ComplianceSummary): boolean {
  const d = windowStartDate(summary);
  if (!d) return true;
  return d.getUTCMonth() === 0 && d.getUTCDate() <= 7;
}

/** Compact label for tiles and chips: "YTD" or "since Jun 1". */
export function windowShort(summary: ComplianceSummary): string {
  const d = windowStartDate(summary);
  if (!d || isCalendarYtd(summary)) return "YTD";
  return (
    "since " +
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    })
  );
}

/** Prose label for sentences: "year to date" or "since June 1, 2026". */
export function windowLong(summary: ComplianceSummary): string {
  const d = windowStartDate(summary);
  if (!d || isCalendarYtd(summary)) return "year to date";
  return (
    "since " +
    d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    })
  );
}
